import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Stethoscope, ShieldCheck, HeartPulse, Building2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/api";
import { useT } from "@/i18n/I18nContext";
import LocaleThemeSwitch from "@/components/common/LocaleThemeSwitch";

export default function Login() {
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const t = useT();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user) navigate("/", { replace: true });
  }, [user, navigate]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      navigate("/", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div data-testid="login-page" className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[#FAFBFC]">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 text-white relative overflow-hidden" style={{ background: "linear-gradient(165deg, #0B7E8C 0%, #095e69 100%)" }}>
        <div className="flex items-center justify-between gap-3 z-10 relative">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center">
              <Stethoscope className="w-6 h-6 text-white" strokeWidth={2.2} />
            </div>
            <div>
              <div className="font-semibold text-lg leading-tight">{t("Hospital at Home")}</div>
              <div className="text-white/70 text-xs tracking-wider uppercase">{t("Care Beyond Walls")}</div>
            </div>
          </div>
          <LocaleThemeSwitch variant="ghost" />
        </div>

        <div className="z-10 relative">
          <div className="text-[11px] tracking-[0.2em] uppercase text-white/70 mb-3">{t("Command Center")}</div>
          <h2 className="text-4xl font-semibold leading-tight max-w-md">
            {t("Run hospital-at-home care with clinical precision.")}
          </h2>
          <p className="mt-4 text-white/80 max-w-md text-[15px]">
            {t("Unified administration for patients, clinicians, facilities, and audit — the foundation for your entire H@H ecosystem.")}
          </p>
          <div className="mt-8 grid grid-cols-3 gap-3 max-w-md">
            {[
              { icon: HeartPulse, label: "Patient Ops" },
              { icon: ShieldCheck, label: "Roles & Audit" },
              { icon: Building2, label: "Facility Network" },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="p-3 rounded-lg bg-white/10 border border-white/15">
                <Icon className="w-5 h-5 text-white mb-2" strokeWidth={2} />
                <div className="text-[12px] font-medium text-white/90">{t(label)}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="z-10 relative text-white/60 text-xs">
          © {new Date().getFullYear()} {t("Hospital at Home Platform")}
        </div>

        <div aria-hidden className="absolute -bottom-32 -right-32 w-[420px] h-[420px] rounded-full bg-white/5" />
        <div aria-hidden className="absolute top-1/3 -left-20 w-[300px] h-[300px] rounded-full bg-white/5" />
      </div>

      {/* Right form panel */}
      <div className="flex items-center justify-center p-6 lg:p-12 relative">
        {/* Mobile-only locale/theme switch — pinned top-right */}
        <div className="lg:hidden absolute top-4 right-4">
          <LocaleThemeSwitch variant="header" />
        </div>

        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-lg bg-[#0B7E8C] flex items-center justify-center">
              <Stethoscope className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-semibold text-[#1F2937]">{t("Hospital at Home")}</div>
              <div className="text-[#6B7280] text-xs uppercase tracking-wider">{t("Care Beyond Walls")}</div>
            </div>
          </div>

          <div className="mb-7">
            <div className="text-[11px] tracking-[0.2em] uppercase text-[#0B7E8C] font-semibold mb-2">
              {t("Sign in")}
            </div>
            <h1 className="text-2xl font-semibold text-[#1F2937]">{t("Welcome back")}</h1>
            <p className="text-sm text-[#6B7280] mt-1">
              {t("Use your H@H administrator credentials to continue.")}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-sm text-[#1F2937]">{t("Work email")}</Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@hah.health"
                className="mt-1.5 h-11 border-[#E5E7EB] focus-visible:ring-[#0B7E8C]"
                required
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-sm text-[#1F2937]">{t("Password")}</Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-1.5 h-11 border-[#E5E7EB] focus-visible:ring-[#0B7E8C]"
                required
              />
            </div>

            {error && (
              <div data-testid="login-error" className="text-sm bg-[#EF444412] border border-[#EF444433] text-[#B91C1C] rounded-lg px-3 py-2">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full h-11 bg-[#0B7E8C] hover:bg-[#128C97] text-white font-medium"
            >
              {loading ? t("Signing in…") : t("Sign in")}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
